/**
 * # dxfBridge.carriedObjectRecords — OBJECTS records a from-scratch export CARRIES
 *
 * An entity can hold a HARD POINTER into the OBJECTS section: an `ACAD_TABLE`'s
 * group 342 names its `TABLESTYLE` by handle. Autodesk's wblock clone "clones
 * hard owners and hard pointers and ignores any soft references" (ObjectARX
 * Developer's Guide, *Deep Clone Basics*), and groups 340–349 are the DXF
 * hard-pointer range — so the pointed-at OBJECT must travel with the entity into
 * the new drawing, exactly as ezdxf files it (`ezdxf/entities/acad_table.py`
 * registers `TABLESTYLE` under `dict_name="ACAD_TABLESTYLE"`).
 *
 * WHY A SEPARATE CARRIER AND NOT `rawObjectGroups`. `rawObjectGroups` is the
 * VERBATIM imported OBJECTS stream and is therefore one of the three
 * by-construction signals `isImportedSourceDocument` reads: a from-scratch
 * document that sets it is re-classified as IMPORTED, `ensureConformantSkeleton`
 * early-returns and the export loses the whole conformant skeleton (measured on
 * the WBLOCK table fixture: 15 967 bytes of DXF collapse to 1 700 — no CLASSES,
 * no root DICTIONARY, no BLOCK_RECORD table, no minted hex handles). A carried
 * record is therefore passed BESIDE the document, as an explicit export input,
 * and is MERGED INTO the skeleton's own OBJECTS stream here — the same shape the
 * ACAD_LAYOUT machinery already uses for a layout that must appear in a
 * dictionary it does not own (`patchLayoutDictionaryEntries`).
 *
 * WHAT THE MERGE GUARANTEES, per record:
 *   1. a FRESH own group-5 handle minted from the skeleton's shared minter, so a
 *      source handle can never alias a minted one;
 *   2. `330` = the owning dictionary's handle, resolved from the ROOT DICTIONARY
 *      by key (`ACAD_TABLESTYLE`, …) and NEVER fabricated — an absent dictionary
 *      means the record is not carried at all;
 *   3. a matching `3 <name>` / `350 <new handle>` child in that dictionary, so
 *      the object is reachable (an unreachable OBJECTS record is what ezdxf's
 *      audit purges);
 *   4. the `102 {ACAD_REACTORS …}` / `102 {ACAD_XDICTIONARY …}` prologue dropped —
 *      its targets belong to the SOURCE drawing's object graph and do not exist
 *      here;
 *   5. every remaining POINTER group whose target is not emitted by this document
 *      dropped (a TABLESTYLE cell style carries `340` → a text STYLE record), so
 *      carrying the style can never introduce a new dangling pointer;
 *   6. an old-handle → new-handle entry the caller feeds to the SAME reference
 *      remap that follows a reminted record (`remapEntityHandleReferences`), so
 *      the referring entity's 342 lands on the merged record.
 *
 * Source of truth: Autodesk DXF reference (DICTIONARY / TABLESTYLE group codes,
 * the 330–369 pointer range) + ezdxf 1.4.3.
 */
import { isTopLevelGroup, trackNestedBlockDepth } from "@dxf-ai-editor/dxf-io";

import { isHexHandle } from "./dxfBridge.conformantSkeleton.handles";

/** One raw DXF group as retained on a document / carried record. */
export interface CarriedRawGroup {
  code: number;
  value: string;
}

/**
 * One OBJECTS-section record an export carries into a from-scratch document on
 * behalf of an entity that hard-points at it.
 */
export interface CarriedObjectRecord {
  /** ROOT DICTIONARY key of the dictionary that owns it, e.g. `ACAD_TABLESTYLE`. */
  dictionaryKey: string;
  /** The `3 <name>` child entry it is registered under (the style name). */
  entryName: string;
  /** Its own group-5 handle in the SOURCE document — what referring entities hold. */
  sourceHandle: string;
  /** Its verbatim groups, `0 <TYPE>` first. */
  groups: CarriedRawGroup[];
}

/** The result of {@link mergeCarriedObjectRecords}. */
export interface CarriedMergeResult {
  /** The OBJECTS stream with every carried record appended + registered. */
  rawObjectGroups: CarriedRawGroup[];
  /** Lowercased SOURCE handle → minted handle, for the entity reference remap. */
  remap: Map<string, string>;
  /** Lowercased minted own handles of the records that were merged. */
  carriedOwnHandles: Set<string>;
}

/**
 * DXF POINTER group codes — the soft-pointer (330–339), hard-pointer (340–349),
 * soft-owner (350–359) and hard-owner (360–369) ranges of the "Common Group
 * Codes for Objects" table. Every one of them holds a HANDLE, so every one of
 * them can dangle once a record is lifted out of its source drawing.
 */
function isPointerCode(code: number): boolean {
  return code >= 330 && code <= 369;
}

/** Record boundaries of a flat OBJECTS group stream (index of each `0 <TYPE>`). */
function recordStartIndices(groups: readonly CarriedRawGroup[]): number[] {
  const starts: number[] = [];
  for (let i = 0; i < groups.length; i += 1) {
    if (groups[i].code === 0) starts.push(i);
  }
  return starts;
}

/**
 * The own group-5 handle of the record occupying `[start, end)` — the FIRST
 * TOP-LEVEL `5` (a `102 {…}` block's inner groups are never the record's own).
 */
function recordOwnHandle(
  groups: readonly CarriedRawGroup[],
  start: number,
  end: number,
): string | undefined {
  let depth = 0;
  for (let i = start + 1; i < end; i += 1) {
    const g = groups[i];
    depth = trackNestedBlockDepth(g, depth);
    if (!isTopLevelGroup(g, depth)) continue;
    if (g.code === 5) {
      const handle = g.value.trim();
      if (handle.length > 0) return handle;
    }
  }
  return undefined;
}

/**
 * The verbatim groups of the OBJECTS record whose own handle is `handle`
 * (case-insensitive — DXF handles are), `0 <TYPE>` first, or `undefined` when
 * the stream carries no such record. Pure: the returned groups are copies.
 */
export function sliceRawObjectRecord(
  rawObjectGroups: readonly CarriedRawGroup[] | undefined,
  handle: string | undefined,
): CarriedRawGroup[] | undefined {
  if (!rawObjectGroups || !handle) return undefined;
  const wanted = handle.trim().toLowerCase();
  if (wanted === "") return undefined;
  const starts = recordStartIndices(rawObjectGroups);
  for (let r = 0; r < starts.length; r += 1) {
    const start = starts[r];
    const end = r + 1 < starts.length ? starts[r + 1] : rawObjectGroups.length;
    if (recordOwnHandle(rawObjectGroups, start, end)?.toLowerCase() !== wanted) {
      continue;
    }
    return rawObjectGroups.slice(start, end).map((g) => ({ ...g }));
  }
  return undefined;
}

/**
 * Strip a carried record down to the groups that can survive the move: drop the
 * `102 {…}` blocks entirely (guarantee 4) and drop the OWNER slot — the first
 * top-level `5` and every top-level `330` BEFORE the `100` subclass marker — so
 * the merge can re-state both against THIS document. Groups after the subclass
 * marker are preserved verbatim, including a cell style's own `330`-range
 * pointers, which guarantee 5 prunes later against the emitted-handle census.
 */
function strippedRecordBody(groups: readonly CarriedRawGroup[]): CarriedRawGroup[] {
  const body: CarriedRawGroup[] = [];
  let depth = 0;
  let sawSubclass = false;
  let droppedOwnHandle = false;
  for (let i = 1; i < groups.length; i += 1) {
    const g = groups[i];
    depth = trackNestedBlockDepth(g, depth);
    if (!isTopLevelGroup(g, depth)) continue;
    if (g.code === 100) sawSubclass = true;
    if (!sawSubclass) {
      if (g.code === 5 && !droppedOwnHandle) {
        droppedOwnHandle = true;
        continue;
      }
      if (g.code === 330) continue;
    }
    body.push({ ...g });
  }
  return body;
}

/**
 * The `[start, end)` group range of the DICTIONARY (or ACDBDICTIONARYWDFLT)
 * record whose own handle is `dictHandle`, or `undefined` when the stream holds
 * no such record. The ONE place that answers "which groups are that
 * dictionary's" — both the read ({@link dictionaryChildHandle}) and the write
 * ({@link appendDictionaryChild}) go through it, so they cannot drift on which
 * record they mean.
 */
function findDictionaryRecordRange(
  groups: readonly CarriedRawGroup[],
  dictHandle: string,
): { start: number; end: number } | undefined {
  const starts = recordStartIndices(groups);
  for (let r = 0; r < starts.length; r += 1) {
    const start = starts[r];
    const end = r + 1 < starts.length ? starts[r + 1] : groups.length;
    const type = groups[start].value.trim().toUpperCase();
    if (type !== "DICTIONARY" && type !== "ACDBDICTIONARYWDFLT") continue;
    if (
      recordOwnHandle(groups, start, end)?.toLowerCase() !==
      dictHandle.toLowerCase()
    ) {
      continue;
    }
    return { start, end };
  }
  return undefined;
}

/**
 * Append a `3 <name>` / `350 <handle>` child to the DICTIONARY record whose own
 * handle is `dictHandle`, PRESERVING every group it already carries.
 *
 * This is the ACAD_LAYOUT machinery's twin, and the difference is deliberate:
 * `patchLayoutDictionaryEntries` REPLACES the child list because it owns every
 * tab in it, while a carried record only ADDS itself to a dictionary whose other
 * children (and their `350` vs `360` ownership codes) are none of its business.
 * Returns a NEW array; unchanged when the dictionary is absent.
 */
function appendDictionaryChild(
  groups: readonly CarriedRawGroup[],
  dictHandle: string,
  name: string,
  handle: string,
): CarriedRawGroup[] {
  const range = findDictionaryRecordRange(groups, dictHandle);
  if (range === undefined) return [...groups];
  return [
    ...groups.slice(0, range.end),
    { code: 3, value: name },
    { code: 350, value: handle },
    ...groups.slice(range.end),
  ];
}

/**
 * The `3 <name>` → `350|360 <handle>` children of the record occupying
 * `[start, end)`, keyed by LOWERCASED name (dictionary keys are matched
 * case-insensitively, as DXF names are throughout). Groups inside a `102 {…}`
 * block are skipped so a reactor payload is never read as a child pair.
 */
function readDictionaryChildren(
  groups: readonly CarriedRawGroup[],
  start: number,
  end: number,
): Map<string, string> {
  const children = new Map<string, string>();
  let depth = 0;
  let pendingName: string | undefined;
  for (let i = start + 1; i < end; i += 1) {
    const g = groups[i];
    depth = trackNestedBlockDepth(g, depth);
    if (!isTopLevelGroup(g, depth)) continue;
    if (g.code === 3) {
      pendingName = g.value.trim();
      continue;
    }
    if ((g.code === 350 || g.code === 360) && pendingName !== undefined) {
      const handle = g.value.trim();
      if (handle.length > 0) children.set(pendingName.toLowerCase(), handle);
      pendingName = undefined;
    }
  }
  return children;
}

/**
 * The handle of the ROOT DICTIONARY's `<key>` child in `groups`. The root
 * dictionary is the FIRST `0 DICTIONARY` record (the OBJECTS section opens with
 * it). `undefined` when the key is absent — a dictionary is never fabricated.
 */
function rootDictionaryChildHandle(
  groups: readonly CarriedRawGroup[],
  key: string,
): string | undefined {
  const starts = recordStartIndices(groups);
  for (let r = 0; r < starts.length; r += 1) {
    const start = starts[r];
    if (groups[start].value.trim().toUpperCase() !== "DICTIONARY") continue;
    const end = r + 1 < starts.length ? starts[r + 1] : groups.length;
    return readDictionaryChildren(groups, start, end).get(key.toLowerCase());
  }
  return undefined;
}

/**
 * The handle already filed under `name` in the dictionary whose own handle is
 * `dictHandle`, or `undefined` when that name is free.
 *
 * A DICTIONARY's keys are UNIQUE — appending a second `3 <same name>` child
 * would make the emitted dictionary invalid, and ezdxf's audit removes the
 * duplicate (taking one of the two objects out of reach with it). So a carried
 * record whose name is already taken is MERGED ONTO the resident one rather
 * than written twice: its referring handle is remapped to the resident record.
 * That is also AutoCAD's own rule for a name clash on a clone — a definition
 * arriving under a name the destination drawing already uses resolves to the
 * RESIDENT definition (Autodesk, ObjectARX *Deep Clone Basics*, the duplicate-
 * record-name resolution the wblock clone applies to symbol / dictionary
 * records).
 */
function dictionaryChildHandle(
  groups: readonly CarriedRawGroup[],
  dictHandle: string,
  name: string,
): string | undefined {
  const range = findDictionaryRecordRange(groups, dictHandle);
  if (range === undefined) return undefined;
  return readDictionaryChildren(groups, range.start, range.end).get(
    name.toLowerCase(),
  );
}

/**
 * Merge `records` into the from-scratch document's OBJECTS stream, minting a
 * fresh own handle for each and registering it under its owning dictionary.
 *
 * A record is SKIPPED (never partially merged) when its owning dictionary is
 * absent from the stream, when its groups are empty / do not open with a `0`
 * type marker, or when its source handle was already carried — duplicates are
 * collapsed onto the first merge so two tables sharing one style ship ONE
 * TABLESTYLE and both 342s land on it.
 *
 * Pure: `rawObjectGroups` is not mutated.
 */
export function mergeCarriedObjectRecords(
  rawObjectGroups: readonly CarriedRawGroup[],
  records: readonly CarriedObjectRecord[],
  mint: () => string,
): CarriedMergeResult {
  let groups: CarriedRawGroup[] = [...rawObjectGroups];
  const remap = new Map<string, string>();
  const carriedOwnHandles = new Set<string>();
  for (const record of records) {
    // A referring handle the EXPORT will not emit is not worth carrying a
    // record for: `HANDLE_FIELD_CLASS`'s `'object'` class keeps a structural
    // reference verbatim when hex and DROPS it otherwise (never emitting an
    // invalid handle), so a non-hex 342 is gone from the entity by the time
    // this record would be reached — and the style would sit in the file with
    // nothing pointing at it. Judged with the SAME predicate that rewrite
    // uses, not a second spelling of it.
    //
    // The remap key is that handle LOWERCASED: DXF handles are
    // case-insensitive and `remapEntityHandleReferences` probes the map with
    // `value.toLowerCase()` on the entity's stored handle. The hex guard above
    // means there is no whitespace left to normalise away.
    if (!isHexHandle(record.sourceHandle)) continue;
    const sourceHandle = record.sourceHandle.toLowerCase();
    if (remap.has(sourceHandle)) continue;
    if (record.groups.length === 0 || record.groups[0].code !== 0) continue;
    const dictHandle = rootDictionaryChildHandle(groups, record.dictionaryKey);
    if (dictHandle === undefined) continue;
    // A name already filed in that dictionary wins — the carried record merges
    // onto the resident one instead of duplicating the key (see
    // {@link dictionaryChildHandle}). The referring entity still resolves, and
    // to a style of the SAME NAME, which is what a style reference means.
    const resident = dictionaryChildHandle(groups, dictHandle, record.entryName);
    if (resident !== undefined) {
      remap.set(sourceHandle, resident);
      continue;
    }
    const newHandle = mint();
    groups = [
      ...groups,
      { code: 0, value: record.groups[0].value },
      { code: 5, value: newHandle },
      { code: 330, value: dictHandle },
      ...strippedRecordBody(record.groups),
    ];
    groups = appendDictionaryChild(groups, dictHandle, record.entryName, newHandle);
    remap.set(sourceHandle, newHandle);
    carriedOwnHandles.add(newHandle.toLowerCase());
  }
  return { rawObjectGroups: groups, remap, carriedOwnHandles };
}

/**
 * Walk the CARRIED records of `groups` (the ones whose minted own handle is in
 * `carriedOwnHandles`) and pass every TOP-LEVEL POINTER group (330–369) through
 * `visit`, which returns the group to emit in its place or `undefined` to DROP
 * it. Everything else — a record this export did not carry, a non-pointer
 * group, a group nested inside a `102 {…}` block — is preserved verbatim.
 *
 * THE ONE walk both carried-pointer passes share ({@link remapCarriedPointers}
 * then {@link dropDanglingCarriedPointers}), so the pass that REWRITES a
 * pointer and the pass that PRUNES one cannot drift on which groups they mean —
 * the drift between them is exactly the defect this walk was extracted to close.
 *
 * `isOwnerSlot` marks the record's `330` OWNER: the first top-level `330` BEFORE
 * the `100` subclass marker. That slot is the one group of a merged record that
 * {@link mergeCarriedObjectRecords} STATED against THIS document (the owning
 * dictionary's handle) instead of copying from the source — the mirror image of
 * the slot {@link strippedRecordBody} drops, defined by the same rule so the two
 * halves of "re-state the owner" stay one statement.
 */
function mapCarriedRecordPointers(
  groups: readonly CarriedRawGroup[],
  carriedOwnHandles: ReadonlySet<string>,
  visit: (
    group: CarriedRawGroup,
    isOwnerSlot: boolean,
  ) => CarriedRawGroup | undefined,
): CarriedRawGroup[] {
  if (carriedOwnHandles.size === 0) return [...groups];
  const starts = recordStartIndices(groups);
  // No `0` marker at all — nothing is a record, so nothing is carried.
  if (starts.length === 0) return [...groups];
  // Groups BEFORE the first record marker (none in a well-formed stream) are
  // preserved so the function is total.
  const out: CarriedRawGroup[] = [...groups.slice(0, starts[0])];
  for (let r = 0; r < starts.length; r += 1) {
    const start = starts[r];
    const end = r + 1 < starts.length ? starts[r + 1] : groups.length;
    const own = recordOwnHandle(groups, start, end)?.toLowerCase();
    if (own === undefined || !carriedOwnHandles.has(own)) {
      for (let i = start; i < end; i += 1) out.push(groups[i]);
      continue;
    }
    let depth = 0;
    let sawSubclass = false;
    let sawOwnerSlot = false;
    for (let i = start; i < end; i += 1) {
      const g = groups[i];
      if (i > start) depth = trackNestedBlockDepth(g, depth);
      const topLevel = i === start || isTopLevelGroup(g, depth);
      if (!topLevel || !isPointerCode(g.code)) {
        if (topLevel && g.code === 100) sawSubclass = true;
        out.push(g);
        continue;
      }
      const isOwnerSlot = !sawSubclass && !sawOwnerSlot && g.code === 330;
      if (isOwnerSlot) sawOwnerSlot = true;
      const next = visit(g, isOwnerSlot);
      if (next !== undefined) out.push(next);
    }
  }
  return out;
}

/**
 * Rewrite every POINTER group (330–369) inside a CARRIED record through
 * `remap` — the old-handle → new-handle map of the record remint
 * (`remintRecordOwnHandles`), joined with the carried merge's own
 * source-handle → minted-handle entries.
 *
 * THE ORDERING INVARIANT (@codex round 18 P2). This runs BEFORE the
 * emitted-handle census that {@link dropDanglingCarriedPointers} is judged
 * against, because a carried record's pointers name handles of the SOURCE
 * drawing and the export RE-MINTS the own handle of any record whose source
 * handle was empty / non-hex / colliding with a skeleton handle. Judging a
 * stale source handle against a census of POST-remint handles is wrong in both
 * directions, and both were measured on a WBLOCKed table whose carried
 * TABLESTYLE holds a `340` at the text STYLE `CellText`:
 *   - source STYLE handle `5` → reminted to `100032`; `5` is absent from the
 *     census, so the census DELETED the `340` — a dependency that is present in
 *     the file under its new handle;
 *   - source STYLE handle `13` → reminted to `100032`; `13` is the handle the
 *     skeleton's `Normal` ACDBPLACEHOLDER already owns, so the census KEPT the
 *     `340` aimed at that placeholder — a hard pointer at the wrong object,
 *     which is worse than a dropped one.
 * It is the ORDER that is the fix, not a special case for group 340: every code
 * the prune judges is a code this pass first rewrites, so no pointer can ever be
 * judged on a handle the export has moved.
 *
 * The record's OWNER slot is the one exception, and not an exception to the
 * remap's REACH: it is not a source pointer at all. `mergeCarriedObjectRecords`
 * stated it against THIS document's dictionary, so following a SOURCE record's
 * remint from it could only re-home the record out of its dictionary.
 *
 * Pure — returns a new array.
 */
export function remapCarriedPointers(
  groups: readonly CarriedRawGroup[],
  carriedOwnHandles: ReadonlySet<string>,
  remap: ReadonlyMap<string, string>,
): CarriedRawGroup[] {
  if (remap.size === 0) return [...groups];
  return mapCarriedRecordPointers(groups, carriedOwnHandles, (g, isOwnerSlot) => {
    if (isOwnerSlot) return g;
    const target = remap.get(g.value.trim().toLowerCase());
    return target === undefined ? g : { code: g.code, value: target };
  });
}

/**
 * Guarantee 5 — drop every POINTER group (330–369) inside a CARRIED record whose
 * target is not in `emittedHandles`, i.e. a pointer the source drawing resolved
 * and this one cannot. Scoped to the carried records by their minted own handles
 * so the skeleton's own OBJECTS stream is never touched (its pointers are
 * internally consistent by construction, and `dropDanglingDictionaryKeys` owns
 * the dictionary-child half of that stream).
 *
 * MUST run AFTER {@link remapCarriedPointers}: the census it is judged against
 * holds POST-remint handles, so a pointer still holding its stale SOURCE handle
 * is judged against the wrong drawing's object graph (see that function).
 *
 * The record's own `5` handle and its `330` owner are not pointer-dangling by
 * construction (the owner is the dictionary that now lists it), so they survive
 * the same rule rather than needing an exception. Pure — returns a new array.
 */
export function dropDanglingCarriedPointers(
  groups: readonly CarriedRawGroup[],
  carriedOwnHandles: ReadonlySet<string>,
  emittedHandles: ReadonlySet<string>,
): CarriedRawGroup[] {
  return mapCarriedRecordPointers(groups, carriedOwnHandles, (g) =>
    emittedHandles.has(g.value.trim().toLowerCase()) ? g : undefined,
  );
}
